import { actionCreatorFactory } from "typescript-fsa";
import { bindThunkAction, isFailure as isFailureAction } from "typescript-fsa-redux-thunk";
import isEqual from "lodash/isEqual";

import {
  EntitiesMap,
  Company,
  CompanyMembership,
  CompanyValue,
  CompanyMembershipSection,
  CompanyPostPin,
  Image,
  Post,
} from "../entities";
import { mergeEntities, isClientId } from "../../lib/entities";
import { DenormalizedSection } from "../../company_memberships/entities";
import { RootState } from "../../state";
import { actionCreatorFactoryForBootstrap } from "../../lib/bootstrap";
import { isFailure } from "../../common/actions/VisitApi";
import {
  CompanyShowResponse,
  requestToLoadCompanyShow,
  CompanyAboutResponse,
  requestToLoadCompanyAbout,
  CompanyMembersResponse,
  requestToLoadCompanyMembers,
  CompanyPostsResponse,
  requestToLoadCompanyPosts,
  CompanyProjectsResponse,
  requestToLoadCompanyProjects,
  SimilarCompaniesResponse,
  requestToLoadSimilarCompanies,
  requestToLikePost,
  requestToUnlikePost,
  requestToLoadPosts,
  requestToLoadPinnablePosts,
  UpdateCompanyResponse,
  requestToUpdateCompany,
  UpdateCompanyValuesResponse,
  requestToUpdateCompanyValues,
  LoadInvisibleCompanyValuesResponse,
  requestToLoadInvisibleCompanyValues,
  requestToUpdatePostPins,
  CreateCompanyPostPayload,
  requestToSendCompanyPost,
  requestToLoadPostPins,
} from "../api";
import {
  requestToUpdateMembershipSection,
  requestToCreateMembershipSection,
  requestToDeleteMembershipSection,
  requestToUpdateMembershipsInBatch,
} from "../../company_memberships/api";
import { companyValueValidatorCreator, membershipSectionValidator } from "../validation";
import { validateAll, EntityValidationMapError } from "../../lib/entities/validation";

const actionCreator = actionCreatorFactory("COMPANIES");
const bootstrapActionCreator = actionCreatorFactoryForBootstrap();

// '/'
export const bootstrapCompanyShow = bootstrapActionCreator<CompanyShowResponse>("companies#show");

export const loadCompanyShow = actionCreator.async<RootState, Company, CompanyShowResponse, Error>("LOAD_COMPANY_SHOW");
export const loadCompanyShowWorker = bindThunkAction(loadCompanyShow, async (params, dispatch) => {
  const req = requestToLoadCompanyShow(params);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

export const loadSimilarCompanies = actionCreator.async<RootState, Company, SimilarCompaniesResponse, Error>(
  "LOAD_SIMILAR_COMPANIES"
);
export const loadSimilarCompaniesWorker = bindThunkAction(loadSimilarCompanies, async (params, dispatch) => {
  const req = requestToLoadSimilarCompanies(params);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

// '/about'
export const bootstrapCompanyAbout = bootstrapActionCreator<CompanyAboutResponse>("companies#about");

export const loadCompanyAbout = actionCreator.async<RootState, Company, CompanyAboutResponse, Error>(
  "LOAD_COMPANY_ABOUT"
);
export const loadCompanyAboutWorker = bindThunkAction(loadCompanyAbout, async (params, dispatch) => {
  const req = requestToLoadCompanyAbout(params);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

// '/members'
export const bootstrapCompanyMembers = bootstrapActionCreator<CompanyMembersResponse>("companies#members");

export const loadCompanyMembers = actionCreator.async<RootState, Company, CompanyMembersResponse, Error>(
  "LOAD_COMPANY_MEMBERS"
);
export const loadCompanyMembersWorker = bindThunkAction(loadCompanyMembers, async (params, dispatch) => {
  const req = requestToLoadCompanyMembers(params);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

// '/feed'
export const bootstrapCompanyPosts = bootstrapActionCreator<CompanyPostsResponse>("companies#feed");

export const loadCompanyPosts = actionCreator.async<RootState, Company, CompanyPostsResponse, Error>(
  "LOAD_COMPANY_POSTS"
);
export const loadCompanyPostsWorker = bindThunkAction(loadCompanyPosts, async (params, dispatch) => {
  const req = requestToLoadCompanyPosts(params);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

// '/projects'
export const bootstrapCompanyProjects = bootstrapActionCreator<CompanyProjectsResponse>("companies#projects");

export const loadCompanyProjects = actionCreator.async<RootState, Company, CompanyProjectsResponse, Error>(
  "LOAD_COMPANY_PROJECTS"
);
export const loadCompanyProjectsWorker = bindThunkAction(loadCompanyProjects, async (params, dispatch) => {
  const req = requestToLoadCompanyProjects(params);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

/**
 * Save all drafts.
 * If the dirty field is not true, skip to request.
 */
export const saveDraftAll = actionCreator.async<RootState, void, void, Error>("SAVE_DRAFT_ALL");

export const saveDraftAllWorker = bindThunkAction(saveDraftAll, async (_params, dispatch, getState) => {
  const {
    common: { isDraftCompanyDirty },
    show: { isDraftCompanyValuesDirty },
    members: { isDraftMembershipsDirty },
    posts: { isDraftPostPinsDirty },
  } = getState().companies;
  if (isDraftCompanyDirty) {
    const res = await dispatch(saveDraftCompanyWorker());
    if (isFailureAction(res)) {
      throw res.error;
    }
  }
  if (isDraftCompanyValuesDirty) {
    const res = await dispatch(saveDraftCompanyValuesWorker());
    if (isFailureAction(res)) {
      throw res.error;
    }
  }
  if (isDraftMembershipsDirty) {
    const res = await dispatch(saveDraftCompanyMembershipsWorker());
    if (isFailureAction(res)) {
      throw res.error;
    }
  }
  if (isDraftPostPinsDirty) {
    const res = await dispatch(saveDraftPostPinsWorker());
    if (isFailureAction(res)) {
      throw res.error;
    }
  }
});

/**
 * Save draft company.
 */
export const saveDraftCompany = actionCreator.async<RootState, void, UpdateCompanyResponse, Error>(
  "SAVE_DRAFT_COMPANY"
);

const saveDraftCompanyWorker = bindThunkAction(saveDraftCompany, async (_params, dispatch, getState) => {
  const {
    entities,
    common: { draftCompany, draftCompanyAvatar, draftCompanyBackground },
  } = getState().companies;
  const avatarNotChanged =
    draftCompanyAvatar &&
    draftCompanyAvatar.id &&
    entities.images[draftCompanyAvatar.id]!.url === draftCompanyAvatar.url;
  const backgroundNotChanged =
    draftCompanyBackground &&
    draftCompanyBackground.id &&
    entities.images[draftCompanyBackground.id]!.url === draftCompanyBackground.url;
  const res = await dispatch(
    requestToUpdateCompany({
      company: draftCompany!,
      avatar: avatarNotChanged ? undefined : draftCompanyAvatar,
      background: backgroundNotChanged ? undefined : draftCompanyBackground,
    })
  );
  if (isFailure(res)) {
    throw res.error;
  }
  return res.payload.body;
});

/**
 * Save draft company values.
 */
type SaveDraftCompanyValueError = EntityValidationMapError<CompanyValue> | Error;
export const saveDraftCompanyValues = actionCreator.async<
  RootState,
  void,
  UpdateCompanyValuesResponse,
  SaveDraftCompanyValueError
>("SAVE_DRAFT_COMPANY_VALUES");

const saveDraftCompanyValuesWorker = bindThunkAction(saveDraftCompanyValues, async (_params, dispatch, getState) => {
  const {
    companies: {
      common: { draftCompany },
      show: { draftCompanyValues },
    },
  } = getState();
  const visibleValues = draftCompanyValues.filter(v => v.rank >= 0);
  const maxValueDescLength = draftCompany!.country === "japan" ? 255 : 500;
  const companyValueValidator = companyValueValidatorCreator(maxValueDescLength);
  validateAll(visibleValues, companyValueValidator);
  const res = await dispatch(
    requestToUpdateCompanyValues({ company: draftCompany!, companyValues: draftCompanyValues })
  );
  if (isFailure(res)) {
    throw res.error;
  }
  return res.payload.body;
});

type saveDraftCompanyMembershipsError = EntityValidationMapError<CompanyMembershipSection> | Error;
/**
 * Save draft company memberships
 */
export const saveDraftCompanyMemberships = actionCreator.async<
  RootState,
  void,
  { sectionIds: EntityId[]; entities: EntitiesMap },
  saveDraftCompanyMembershipsError
>("SAVE_DRAFT_COMPANY_MEMBERSHIPS");

const saveDraftCompanyMembershipsWorker = bindThunkAction(
  saveDraftCompanyMemberships,
  async (_params, dispatch, getState) => {
    const {
      entities,
      common: { companyId },
      members: { sectionIds, draftMembershipSections, draftFeaturedMemberships },
    } = getState().companies;
    // Validate sctions
    validateAll(draftMembershipSections.map(s => s.section!).filter(Boolean), membershipSectionValidator);
    // Update, create or delete CompanyMembershipSection
    const sectionsToUpdate: CompanyMembershipSection[] = [];
    const sectionsToCreate: CompanyMembershipSection[] = [];
    const sectionIdsToDelete: EntityId[] = [];
    const existingSectionIds: EntityId[] = [];
    draftMembershipSections.forEach((sec, index) => {
      if (!sec.section) {
        return;
      }
      const section = { ...sec.section, display_order: index + 1 };
      if (isClientId(section.id)) {
        sectionsToCreate.push({ ...section, company: companyId });
      } else {
        if (!isEqual(section, entities.company_membership_sections[section.id])) {
          sectionsToUpdate.push(section);
        }
      }
      existingSectionIds.push(section.id);
    });
    sectionIds.filter(secId => !existingSectionIds.includes(secId)).forEach(secId => {
      sectionIdsToDelete.push(secId);
    });
    let newEntities: EntitiesMap = entities;
    let allRes = await Promise.all(
      sectionsToUpdate.map(section => {
        return dispatch(requestToUpdateMembershipSection(section));
      })
    );
    allRes.forEach(res => {
      if (isFailure(res)) {
        throw res.error!;
      } else {
        newEntities = mergeEntities(newEntities, res.payload.body.entities);
      }
    });
    allRes = await Promise.all(
      sectionsToCreate.map(section => {
        return dispatch(requestToCreateMembershipSection(section));
      })
    );
    const clientIdsMap: { [key: string]: string } = {}; // client-generated ID => server-generated ID
    allRes.forEach((res, index) => {
      if (isFailure(res)) {
        throw res.error!;
      } else {
        newEntities = mergeEntities(newEntities, res.payload.body.entities);
        const section = sectionsToCreate[index];
        clientIdsMap[section.id] = res.payload.body!.result;
      }
    });
    await Promise.all(
      sectionIdsToDelete.map(id => {
        return dispatch(requestToDeleteMembershipSection(id));
      })
    );
    // Result of updated sections.
    const newSectionIds = draftMembershipSections
      .map(sec => sec.section && sec.section.id)
      .filter(Boolean)
      .map(id => clientIdsMap[id!] || id!);
    // Update memberships
    const membershipsToUpdate: CompanyMembership[] = [];
    const featuredIds = draftFeaturedMemberships.map(m => m.id);
    draftMembershipSections.forEach(sec => {
      sec.memberships.forEach((membership, index) => {
        const featuredIndex = featuredIds.indexOf(membership.id);
        const newMembership = {
          ...membership,
          display_order: index + 1,
          featured_display_order: featuredIndex >= 0 ? featuredIndex + 1 : -1,
          section: sec.section ? clientIdsMap[sec.section.id] || sec.section.id : null,
        };
        // Request to change only for changed membership
        if (!isEqual(newMembership, entities.company_memberships[membership.id])) {
          membershipsToUpdate.push(newMembership);
        }
      });
    });
    const res = await dispatch(requestToUpdateMembershipsInBatch(membershipsToUpdate));
    if (isFailure(res)) {
      throw res.error!;
    } else {
      newEntities = mergeEntities(newEntities, res.payload.body.entities);
    }
    return { sectionIds: newSectionIds, entities: newEntities };
  }
);

/**
 * Save draft company post pins
 */

export const saveDraftPostPins = actionCreator.async<RootState, null, CompanyPostPin[], Error>("SAVE_DRAFT_POST_PINS");

export const saveDraftPostPinsWorker = bindThunkAction(saveDraftPostPins, async (_params, dispatch, getState) => {
  const {
    common: { companyId },
    posts: { draftPostPins },
  } = getState().companies;
  // TODO(stomk): Save only dirty ones
  const allRes = await Promise.all(
    draftPostPins.map(pin => {
      return dispatch(requestToUpdatePostPins(pin));
    })
  );
  allRes.forEach(res => {
    if (isFailure(res)) {
      throw res.error;
    }
  });
  // Refresh
  const res = await dispatch(requestToLoadPostPins({ companyId }));
  if (isFailure(res)) {
    throw res.error;
  }
  const postPins: CompanyPostPin[] = [];
  res.payload.body.forEach(rawPin => {
    if (!rawPin.pin_id) {
      return;
    }
    postPins.push({
      id: rawPin.pin_id.toString(),
      category_cd: rawPin.category_cd,
      company: companyId,
      post: rawPin.post.id.toString(),
    });
  });
  return postPins;
});

export const likePost = actionCreator.async<RootState, Post, null, Error>("LIKE");
export const unlikePost = actionCreator.async<RootState, Post, null, Error>("UNLIKE");

export const likePostWorker = bindThunkAction(likePost, async (params, dispatch) => {
  const req = requestToLikePost(params);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  return null;
});

export const unlikePostWorker = bindThunkAction(unlikePost, async (params, dispatch) => {
  const req = requestToUnlikePost(params);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  return null;
});

/**
 * Update drafts without saving
 */
export const draftCompanyUpdated = actionCreator<Company>("DRAFT_COMPANY_UPDATED");
export const draftCompanyAvatarUpdated = actionCreator<Image>("DRAFT_COMPANY_AVATAR_UPDATED");
export const draftCompanyBackgroundUpdated = actionCreator<Image>("DRAFT_COMPANY_BACKGROUND_UPDATED");

export const draftCompanyValuesUpdated = actionCreator<{ companyValues: CompanyValue[] }>(
  "DRAFT_COMPANY_VALUES_UPDATED"
);

export const draftMembershipsChanged = actionCreator<{
  membershipSections: DenormalizedSection[];
  featuredMemberships: CompanyMembership[];
}>("DRAFT_MEMBERSHIPS_CHANGED");

export const draftPostPinUpdated = actionCreator<{ companyId: EntityId; postId: EntityId | null; categoryCd: number }>(
  "DRAFT_POST_PINS_UPDATED"
);

/**
 * Load next posts for infinite scroll
 */
export const loadNextPosts = actionCreator.async<
  RootState,
  { companyId: string },
  { posts: EntityId[]; entities: EntitiesMap; meta: { isLastPage: boolean } },
  Error
>("LOAD_NEXT_POSTS");

export const loadNextPostsWorker = bindThunkAction(loadNextPosts, async (params, dispatch, getState) => {
  const { currentPage } = getState().companies.posts;
  const { companyId } = params;
  const req = requestToLoadPosts({ companyId, page: currentPage + 1 });
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  return {
    posts: res.payload.body.result,
    entities: res.payload.body.entities,
    meta: { isLastPage: res.payload.headers["X-List-IsLastPage"]! },
  };
});

/**
 * Load pinnable posts
 */
export const loadPinnablePosts = actionCreator.async<
  RootState,
  { companyId: string },
  { posts: EntityId[]; entities: EntitiesMap },
  Error
>("LOAD_PINNABLE_POSTS");

export const loadPinnablePostsWorker = bindThunkAction(loadPinnablePosts, async (params, dispatch) => {
  const req = requestToLoadPinnablePosts({ companyId: params.companyId });
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  return {
    posts: res.payload.body.result,
    entities: res.payload.body.entities,
  };
});

export const loadInvisibleCompanyValues = actionCreator.async<
  RootState,
  Company,
  LoadInvisibleCompanyValuesResponse,
  Error
>("LOAD_INVISIBLE_COMPANY_VALUES");

export const loadInvisibleCompanyValuesWorker = bindThunkAction(
  loadInvisibleCompanyValues,
  async (company, dispatch) => {
    const res = await dispatch(requestToLoadInvisibleCompanyValues({ companyId: company.id }));
    if (isFailure(res)) {
      throw res.error;
    }
    return res.payload.body;
  }
);

/**
 * Switch editable mode
 */
export const editModeStarted = actionCreator("EDIT_MODE_STARTED");
export const editModeFinished = actionCreator("EDIT_MODE_FINISHED");

/**
 * Create company feed post
 */
export const sendCompanyPost = actionCreator.async<
  RootState,
  CreateCompanyPostPayload,
  { post: EntityId; entities: EntitiesMap },
  Error
>("SEND_COMPANY_POST");
export const sendCompanyPostWorker = bindThunkAction(sendCompanyPost, async (params, dispatch) => {
  const res = await dispatch(requestToSendCompanyPost(params));
  if (isFailure(res)) {
    throw res.payload.body;
  }

  return {
    post: res.payload.body.result,
    entities: res.payload.body.entities,
  };
});

/**
 * Reset current draft
 */

export const draftReset = actionCreator<EntitiesMap>("DRAFT_RESET");



// WEBPACK FOOTER //
// ./frontend/src/companies/actions/index.ts